/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Box } from '../Box';
import { Text } from '../Text';
import { IconName } from '../Icon/Icon.types';
import { Icon } from '../Icon';
import { LoadCard } from 'src/pages/servers/manage-server/ServerUsage/LoadCard/LoadCard';
import { DiskUsageCard } from 'src/pages/servers/manage-server/ServerUsage/DiskUsageCard/DiskUsageCard';
import { MemoryUsageCard } from 'src/pages/servers/manage-server/ServerUsage/MemoryUsageCard/MemoryUsageCard';
import { AppState } from 'src/store';
import { viewportSelectors } from 'src/containers/Viewport/_redux/selectors';
import { connect } from 'react-redux';
import { useEffect, useState } from 'react';
import { ServerSpecs } from 'src/orchd-client';
import { serversApi } from 'src/api_services/servers/service';

interface InfoBlockProps {
  label: string;
  text: string;
  icon: IconName;
  isMobile: boolean;
}

const InfoBlock = ({ label, text, icon, isMobile }: InfoBlockProps) => (
  <Box bg="grey5" h="30px" a="center" j="space-between" d="row" px="sm" mb="sm">
    <Box w="auto" a="center" maxW="80%">
      <Icon name={icon} marginRight="sm" width="auto" />
      {!isMobile && (
        <Text
          size="xs"
          weight="normal"
          transform="uppercase"
          fullWidth={false}
          wordBreak="break-all"
          ellipsis
          maxWidth="auto"
        >
          {label}
        </Text>
      )}
    </Box>
    <Box w="auto" maxW="80%">
      <Text size="sm" align="right" fullWidth wordBreak="break-all" ellipsis>
        {text}
      </Text>
    </Box>
  </Box>
);

interface Props {
  serverId: string;
}

export const stateToProps = (state: AppState) => ({
  isMobile: viewportSelectors.isMobile(state),
});

type StateProps = ReturnType<typeof stateToProps>;

const _ServerInfoDash = ({ serverId, isMobile }: Props & StateProps) => {
  const dummyServer = {
    id: serverId,
    groupId: 'j',
    isControlPanel: false,
    isConfigured: false,
    friendlyName: 'hey friendly',
    hostname: 'hey hostname',
    ips: [],
    roles: {},
    createdAt: 'asd',
    isDecommissioned: false,
    disks: [],
  };

  const [serverSpecs, setServerSpecs] = useState<ServerSpecs>();

  useEffect(() => {
    serversApi
      .getServerSpecs({
        serverId,
      })
      .then((specs) => {
        setServerSpecs(specs.data);
      });
  }, [serverId]);

  return (
    <Box mb="lg">
      <Box bg="light" p="xl" d="row" mb="lg">
        <Box w={{ xs: 12, md: 2 }}>
          <Text
            marginRight="sm"
            weight="normal"
            size="lg"
            ellipsis
            maxWidth="200px"
            marginBottom={{ xs: 'md', md: 'xs' }}
          >
            System info
          </Text>
        </Box>
        <Box w={{ xs: 12, md: 10 }} g="sm">
          <Box w={{ xs: 12, md: 4 }} d="column">
            <InfoBlock label="OS" text={serverSpecs?.os || 'Loading...'} icon="screen" isMobile={isMobile} />
            <InfoBlock
              label="Speed"
              text={serverSpecs ? `${serverSpecs.cpuMhz}Mhz` : 'Loading...'}
              icon="speed"
              isMobile={isMobile}
            />
          </Box>
          <Box w={{ xs: 12, md: 8 }} d="column">
            <Box w={12} d="row" g="sm">
              <Box w={{ xs: 12, md: 6 }}>
                <InfoBlock label="Architecture" text="x86_64" icon="arch" isMobile={isMobile} />
              </Box>
              <Box w={{ xs: 12, md: 6 }}>
                <InfoBlock
                  label="Cores"
                  text={serverSpecs?.cpuCores.toString() || 'Loading...'}
                  icon="cpu_cores"
                  isMobile={isMobile}
                />
              </Box>
            </Box>
            <Box w={12} d="column">
              <InfoBlock label="CPU" text={serverSpecs?.cpuKind || 'Loading...'} icon="cpu" isMobile={isMobile} />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box d="row" g="lg" mb="lg">
        <Box w={{ xs: 12, md: 8 }} mb={{ xs: 'lg', md: '0' }}>
          <LoadCard server={dummyServer} />
        </Box>
        <Box w={{ xs: 12, md: 4 }}>
          <DiskUsageCard server={dummyServer} />
        </Box>
      </Box>
      <Box g="lg">
        <Box w={{ xs: 12, md: 6 }} mb={{ xs: 'lg', md: '0' }}>
          <MemoryUsageCard server={dummyServer} query="ram" />
        </Box>
        <Box w={{ xs: 12, md: 6 }} mb={{ xs: 'lg', md: '0' }}>
          <MemoryUsageCard server={dummyServer} query="swap" />
        </Box>
      </Box>
    </Box>
  );
};

export const ServerInfoDash = connect(stateToProps, undefined)(_ServerInfoDash);
